import { Component, ComponentInterface, Element, Method, State, h } from "@stencil/core";
import { store } from "@stencil/redux";
import AppDatabase, { ProjectAccessDeniedError, ProjectDeletedError, ProjectFreeTrialExpiredError, ProjectSyncError, ProjectSyncStep, ProjectSyncStepState, ProjectUploadError, ProjectUserAbortError } from '../../services/db/app-db.service';
import themeService from '../../services/theme.service';
import { MediaDownloadState, MediaDownloadStatus } from '../../store/app/app.reducer';
import { ApplicationState } from '../../store/reducers';
import { getAppAttachmentDownloadState, getAppAttachmentsShouldDownload, getAppId, getAppMediaDownloadState, getAppNetworkConnected } from '../../store/selectors';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { format } from "date-fns/esm";

const SYNC_MODAL_DISMISS_TIMER = 3000;
const SYNC_MODAL_ABORT_TIMER = 1000;

@Component({
  tag: 'app-sync-modal',
  styleUrl: './sync-modal.scss'
})
export class AppSyncModal implements ComponentInterface {

  @Element() el: HTMLElement;

  @State() steps: ProjectSyncStepState[];
  @State() usersRemaining: number;
  @State() recordsRemaining: number;
  @State() totalRecords: number;
  @State() recordsProgress: number;
  @State() isOffline: boolean;
  @State() complete: boolean = false;
  @State() attachmentsSyncEnabled: boolean;
  @State() mediaDownloadState: MediaDownloadState;
  @State() attachmentDownloadState: MediaDownloadState;
  @State() installation: boolean = false;
  @State() accessError: ProjectAccessDeniedError | ProjectFreeTrialExpiredError;
  @State() syncError: string;

  @State() isOpen: boolean = false;
  @State() aborted: boolean = false;
  @State() aborting: boolean = false;

  @Method() async close() {
    this.dismiss();
  }

  private dismissTimer: number;
  private $rmListeners$: (() => void)[] = [];

  async componentWillLoad() {

    this.$rmListeners$.push(store.mapStateToProps(this, (state: ApplicationState) => {
      const appId = getAppId(state);
      return {
        mediaDownloadState: getAppMediaDownloadState(appId)(state),
        attachmentDownloadState: getAppAttachmentDownloadState(appId)(state),
        attachmentsSyncEnabled: getAppAttachmentsShouldDownload(state),
        isOffline: !getAppNetworkConnected(state)
      };
    }));

    AppDatabase.instance.events.addListener('syncStart', this.handleSyncStart);
    AppDatabase.instance.events.addListener('syncProgress', this.handleSyncProgress);
    AppDatabase.instance.events.addListener('syncComplete', this.handleSyncComplete);
    AppDatabase.instance.events.addListener('syncError', this.handleSyncError);
  }

  disconnectedCallback(): void {
    this.$rmListeners$.map(r => r());
    this.$rmListeners$.length = 0;

    AppDatabase.instance.events.removeListener('syncStart', this.handleSyncStart);
    AppDatabase.instance.events.removeListener('syncProgress', this.handleSyncProgress);
    AppDatabase.instance.events.removeListener('syncComplete', this.handleSyncComplete);
    AppDatabase.instance.events.removeListener('syncError', this.handleSyncError);
  }

  dismiss = () => {
    this.isOpen = false;
    if (this.dismissTimer) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = undefined;
    }
  }

  async keepAwake(): Promise<void> {
    await KeepAwake.keepAwake();
  }

  async allowSleep(): Promise<void> {
    await KeepAwake.allowSleep();
  }

  handleSyncStart = (_id: number, force: boolean) => {
    this.keepAwake();
    this.steps = [
      ProjectSyncStepState.NOT_STARTED,
      ProjectSyncStepState.NOT_STARTED,
      ProjectSyncStepState.NOT_STARTED,
      ProjectSyncStepState.NOT_STARTED
    ];
    this.complete = false;
    this.aborted = false;
    this.aborting = false;
    this.syncError = null;
    this.accessError = null;
    this.isOpen = true;
    this.installation = force === true;
  }

  handleAbort = () => {
    this.allowSleep();
    if (!this.aborting) {
      AppDatabase.instance.abortSync();
      this.aborting = true;
    }
  }

  handleSyncProgress = (step: ProjectSyncStep, status: ProjectSyncStepState, progress: number) => {
    this.setStep(step, status);

    if (step === ProjectSyncStep.RECORDS && progress) {
      this.recordsProgress = progress;
    }
    if (step === ProjectSyncStep.RECORDS && status === ProjectSyncStepState.COMPLETE) {
      this.recordsProgress = 1;
    }
  }

  handleSyncComplete = () => {
    this.allowSleep();
    this.complete = true;
    if (!this.syncError && !this.accessError) {
      this.dismissTimer = window.setTimeout(this.dismiss, this.installation ? 0 : SYNC_MODAL_DISMISS_TIMER);
    }
  }

  handleSyncError = (error: ProjectSyncError) => {
    this.allowSleep();
    switch (error.constructor) {
      case ProjectUserAbortError: {
        this.steps = [
          ProjectSyncStepState.CANCELLED,
          ProjectSyncStepState.CANCELLED,
          ProjectSyncStepState.CANCELLED,
          ProjectSyncStepState.CANCELLED,
        ];
        this.aborted = true;
        setTimeout(this.dismiss, SYNC_MODAL_ABORT_TIMER);
        break;
      }
      case ProjectFreeTrialExpiredError:
      case ProjectAccessDeniedError: {
        this.steps = [
          ProjectSyncStepState.ERROR,
          ProjectSyncStepState.ERROR,
          ProjectSyncStepState.ERROR,
          ProjectSyncStepState.ERROR,
        ];
        this.accessError = error;
        break;
      }
      case ProjectUploadError: {
        this.syncError = 'There was an error uploading your record changes. Please review and try again';
        break;
      }
      case ProjectDeletedError: {
        const date = new Date((error as ProjectDeletedError).date);
        const dateFormatted = format(date, 'dd/MM/yyyy');
        this.syncError = `This project was deleted on ${dateFormatted}. Please check with your project administrator.`;
        break;
      }
    }
  };

  private setStep(id: ProjectSyncStep, state: ProjectSyncStepState) {
    this.steps = [
      ...this.steps.slice(0, id),
      state,
      ...this.steps.slice(id + 1)
    ];
  }

  renderStep(id: number) {
    if (!this.isOpen) {
      return null;
    }

    switch (this.steps[id]) {
      case ProjectSyncStepState.NOT_STARTED: {
        return null;
      }
      case ProjectSyncStepState.RUNNING: {
        return <ion-spinner slot="end" />;
      }
      case ProjectSyncStepState.COMPLETE: {
        return <ion-icon name="checkmark" slot="end" color="success" />;
      }
      case ProjectSyncStepState.ERROR: {
        return <ion-icon name="close-outline" slot="end" color="danger" />;
      }
      case ProjectSyncStepState.CANCELLED: {
        return <ion-icon name="ban-outline" slot="end" />;
      }
    }
  }

  renderBackgroundStep(state: MediaDownloadState) {
    if (this.syncError || this.aborted) {
      return <ion-icon name="ban-outline" slot="end" />;
    }

    if (!state || state.state === MediaDownloadStatus.IDLE) {
      return null;
    }

    switch (state.state) {
      case MediaDownloadStatus.COMPLETE: {
        return <ion-icon name="checkmark" slot="end" color="success" />;
      }
      default: {
        return <ion-spinner slot="end" />;
      }
    }
  }

  renderOffline() {
    return <ion-content forceOverscroll={false}>
      <div class="sync-modal--container">
        <ion-icon name="cloud-offline-outline"></ion-icon>
        <h3>You are offline</h3>
        <p>Try again when you have a network connection</p>
        <ion-button expand="block" onClick={this.dismiss}>Back to Project</ion-button>
      </div>
    </ion-content>
  }

  renderLoading() {
    return <ion-content forceOverscroll={false}>
      <div class="sync-modal--container">
        <ion-spinner />
      </div>
    </ion-content>;
  }

  renderAccessError() {
    let message: string;
    let icon: string;
    let title: string;

    switch (this.accessError.constructor) {
      case ProjectAccessDeniedError: {
        title = "Access Denied";
        message = "You do not have access to this project. Please check with your organisation administrator.";
        icon = "lock-closed-outline";
        break;
      }
      case ProjectFreeTrialExpiredError: {
        title = "Organisation Paused";
        message = "The organisation that owns this project has been paused. It was on a free trial & needs updating to a full account by the organisation owner.";
        icon = "time-outline";
        break;
      }
    }

    return <ion-content forceOverscroll={false}>
      <div class="sync-modal--container">
        <ion-icon name={icon}></ion-icon>
        <h3>{title}</h3>
        <p>{message}</p>
        <ion-button expand="block" onClick={this.dismiss}>Close</ion-button>
      </div>
    </ion-content>
  }


  renderContent() {
    if (typeof this.isOffline === 'undefined') {
      return this.renderLoading();
    }
    if (this.isOffline) {
      return this.renderOffline();
    }

    if (this.accessError) {
      return this.renderAccessError();
    }

    const title = this.installation ? 'Installing project' : 'Syncing project';

    return [<ion-header>
      <ion-toolbar>
        <ion-title>{title}</ion-title>
        <ion-buttons slot="end">
          {!this.complete && <ion-button color="danger" onClick={this.handleAbort} disabled={this.aborting}>Abort</ion-button>}
          {this.complete && <ion-button color="primary" onClick={this.dismiss}>Close</ion-button>}
        </ion-buttons>
      </ion-toolbar>
    </ion-header>,
    <ion-content class={themeService.SELECTOR} forceOverscroll={false}>
      <ion-list lines="none">

        {this.syncError && <ion-item color="warning" >
          <ion-text class="ion-padding">{this.syncError}</ion-text>
          <ion-button onClick={this.dismiss}>Close</ion-button>
        </ion-item>}

        <ion-item>
          <ion-icon slot="start" name="settings-outline" />
          <ion-label>Config</ion-label>
          {this.renderStep(0)}
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="people-outline" />

          <ion-label>Users</ion-label>
          {this.renderStep(1)}
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="cloud-download-outline" />
          <ion-label>Download Records</ion-label>
          {this.renderStep(2)}
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="cloud-upload-outline" />
          <ion-label>Upload Records</ion-label>
          {this.renderStep(3)}
        </ion-item>

        <ion-item>
          <ion-icon slot="start" name="images-outline" />
          <ion-label>Sync Media</ion-label>
          {this.renderBackgroundStep(this.mediaDownloadState)}
        </ion-item>

        {this.attachmentsSyncEnabled && <ion-item>
          <ion-icon slot="start" name="images-outline" />
          <ion-label>Download Attachments</ion-label>
          {this.renderBackgroundStep(this.attachmentDownloadState)}
        </ion-item>}
      </ion-list>
    </ion-content>,
    <ion-footer class={{ hidden: this.installation }}>
      <ion-toolbar>
        <ion-button size="large" expand="block" disabled={!this.complete} class={{ 'sync-done': true, complete: this.complete, aborted: this.aborted, 'coreo-button': true }} onClick={this.dismiss}>{this.aborted ? 'Aborted' : 'Done'}</ion-button>
      </ion-toolbar>
    </ion-footer>
    ];
  }

  render() {
    return <ion-modal
      isOpen={this.isOpen}
      initialBreakpoint={1.0}
      breakpoints={[1.0]}
      backdropDismiss={false}
      class={`sync-modal ${this.installation ? 'installation' : ''}`}
      handle={false} showBackdrop>
      {this.renderContent()}
    </ion-modal>
  }
}
